import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert } from '@/ui/Alert/Alert'
import { Avatar } from '@/ui/Avatar/Avatar'
import { Button } from '@/ui/Button/Button'
import { Card } from '@/ui/Card/Card'
import { Code } from '@/ui/Code/Code'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Field, Input, Textarea } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { Link } from '@/ui/Link/Link'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { ConfirmDialog } from '@/ui/Modal/Modal'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { lifecycleTone } from '@/ui/Status/lifecycle'
import { StatusBadge, StatusDot } from '@/ui/Status/Status'
import { agentDefsApi } from '../api/agents'
import type { ApiResponse } from '../api/client'
import { memoryApi } from '../api/memory'
import { RunsExplorer } from '../components/RunsExplorer'
import { WsPageShell } from '../components/WsPageShell'
import { ROUTES } from '../constants/routes'
import { profilesCopy, runsCopy } from '../copy'
import { agentQueryKeys, useMeQuery, useThreadsQuery } from '../hooks/queries'
import { useMutationToast } from '../hooks/useMutationToast'
import { useNow } from '../hooks/useNow'
import { useTopicsQuery } from '../hooks/useTopicsQuery'
import { parseStreamKey, useStreamStore } from '../store/stream'
import { useToastStore } from '../store/toast'
import type { AgentDefInfo, MemoryEntry } from '../types/api'
import { formatDateTime, formatRelative } from '../utils/time'
import styles from './AgentProfilePage.module.css'

/**
 * The agent's written memory — entries it recorded for itself with
 * `memory_write`, read straight from the store (works while the agent is
 * stopped). Managers can add or delete entries; the list ends with its add
 * row, which opens the form in place (D213: a list creates from its end).
 */
function AgentMemorySection({
  wsId,
  agentId,
  canManage,
}: {
  wsId: string
  agentId: string
  canManage: boolean
}) {
  const qc = useQueryClient()
  const formId = useId()
  const [deletingKey, setDeletingKey] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [newKey, setNewKey] = useState('')
  const [newText, setNewText] = useState('')

  const { data, isLoading, error, refetch } = useQuery<MemoryEntry[]>({
    queryKey: ['memory', wsId, agentId],
    queryFn: () => memoryApi.list(wsId, agentId),
    enabled: !!agentId,
    staleTime: 10_000,
    retry: false,
  })
  const showSkeleton = useSkeletonDelay(isLoading)
  const entries = data ?? []

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ['memory', wsId, agentId] })
  }

  const writeMut = useMutationToast({
    mutationFn: () => memoryApi.write(wsId, agentId, newKey.trim(), newText),
    successMessage: profilesCopy.memory.saved,
    errorMessage: profilesCopy.memory.saveFailed,
    onSuccess: () => {
      setAdding(false)
      setNewKey('')
      setNewText('')
      invalidate()
    },
  })

  const deleteMut = useMutationToast({
    mutationFn: (key: string) => memoryApi.remove(wsId, agentId, key),
    successMessage: profilesCopy.memory.deleted,
    errorMessage: profilesCopy.memory.deleteFailed,
    onSuccess: () => {
      setDeletingKey(null)
      invalidate()
    },
  })

  const addEntry = (
    <Button variant="ghost" onClick={() => setAdding(true)}>
      <Icon name="plus" size="icon-embedded" />
      {profilesCopy.memory.add}
    </Button>
  )

  return (
    <Stack as="section" gap="space-12">
      <SectionHeader rule>{profilesCopy.memory.title}</SectionHeader>

      {error && data && (
        <Alert tone="warning">
          <Inline gap="space-8" align="baseline">
            <Text>{profilesCopy.refreshMemoryFailed}</Text>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {profilesCopy.retry}
            </Button>
          </Inline>
        </Alert>
      )}
      {isLoading ? (
        <div aria-busy="true">{showSkeleton && <Skeleton lines={3} />}</div>
      ) : error && !data ? (
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline">
            <Text>{profilesCopy.memory.loadFailed}</Text>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {profilesCopy.retry}
            </Button>
          </Inline>
        </Alert>
      ) : entries.length === 0 && !(canManage && adding) ? (
        <EmptyState
          variant="inline"
          description={profilesCopy.memory.empty}
          action={canManage ? addEntry : undefined}
        />
      ) : (
        <RowList>
          {entries.map((e) => (
            <RowListItem key={e.key}>
              <Code>{e.key}</Code>
              <Text tone="text-sub" className={styles.entryText}>
                {e.text}
              </Text>
              <Text variant="caption" tone="text-mute" numeric>
                {formatDateTime(e.created_at)}
              </Text>
              {canManage && (
                <Button
                  variant="ghost"
                  aria-label={profilesCopy.deleteEntry(e.key)}
                  onClick={() => setDeletingKey(e.key)}
                >
                  {profilesCopy.memory.delete}
                </Button>
              )}
            </RowListItem>
          ))}
          {canManage &&
            (adding ? (
              <RowListItem className={styles.form}>
                <form
                  onSubmit={(ev) => {
                    ev.preventDefault()
                    if (newKey.trim()) writeMut.mutate()
                  }}
                >
                  <Stack gap="space-16">
                    <Field
                      id={`${formId}-key`}
                      label={profilesCopy.memory.keyLabel}
                      help={profilesCopy.memory.keyHelp}
                    >
                      <Input
                        autoFocus
                        value={newKey}
                        onChange={(ev) => setNewKey(ev.target.value)}
                        placeholder={profilesCopy.memory.keyPlaceholder}
                      />
                    </Field>
                    <Field
                      id={`${formId}-text`}
                      label={profilesCopy.memory.textLabel}
                    >
                      <Textarea
                        value={newText}
                        onChange={(ev) => setNewText(ev.target.value)}
                        rows={5}
                      />
                    </Field>
                    <Inline gap="space-8">
                      <Button
                        type="submit"
                        disabled={!newKey.trim()}
                        loading={writeMut.isPending}
                      >
                        {profilesCopy.memory.save}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setAdding(false)}
                      >
                        {profilesCopy.memory.cancel}
                      </Button>
                    </Inline>
                  </Stack>
                </form>
              </RowListItem>
            ) : (
              <RowListItem>{addEntry}</RowListItem>
            ))}
        </RowList>
      )}

      <ConfirmDialog
        open={deletingKey !== null}
        title={profilesCopy.memory.deleteTitle}
        tone="danger"
        description={
          deletingKey
            ? profilesCopy.memory.deleteDescription(deletingKey)
            : undefined
        }
        confirmLabel={profilesCopy.memory.deleteConfirm}
        loading={deleteMut.isPending}
        onConfirm={() => {
          if (deletingKey) deleteMut.mutate(deletingKey)
        }}
        onClose={() => setDeletingKey(null)}
      />
    </Stack>
  )
}

/**
 * Full-width agent profile (redesign §7): identity header with pause/edit/
 * delete, live RUNNING NOW jobs with jump links, and FINISHED RECENTLY runs.
 */
type ProfileLifecycleKind = 'pause' | 'restore' | 'delete'

export function AgentProfilePage() {
  const { workspaceId = '', agentId = '' } = useParams<{
    workspaceId: string
    agentId: string
  }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const addToast = useToastStore((state) => state.addToast)
  const now = useNow()
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  // The lifecycle request in flight: its button shows pending (loading).
  const [lifecycleAction, setLifecycleAction] = useState<
    ProfileLifecycleKind | 'reconcile' | null
  >(null)
  const lifecycleBusy = lifecycleAction !== null
  const [pendingReconciliation, setPendingReconciliation] = useState(false)
  const retryTimer = useRef<
    ReturnType<typeof globalThis.setTimeout> | undefined
  >(undefined)

  const { data: me } = useMeQuery()
  const ws = me?.workspaces.find((w) => w.workspace_id === workspaceId)
  const canManage = ws?.role === 'owner' || ws?.role === 'admin'

  const agentQuery = useQuery<AgentDefInfo>({
    queryKey: agentQueryKeys.detail(workspaceId, agentId),
    queryFn: () => agentDefsApi.get(workspaceId, agentId),
    enabled: !!workspaceId && !!agentId,
    staleTime: 10_000,
  })
  const agent = agentQuery.data
  const showAgentSkeleton = useSkeletonDelay(agentQuery.isPending)

  const { data: topics = [] } = useTopicsQuery(workspaceId)
  const topicById = new Map(topics.map((t) => [t.topic_id, t]))

  // Live jobs for this agent, from the authoritative turn store.
  const activeTurns = useStreamStore((s) => s.activeTurns)
  const streamTools = useStreamStore((s) => s.tools)
  const jobs: {
    key: string
    topicId: string
    threadId: string | null
    tool: { tool_name: string } | undefined
  }[] = []
  for (const key in activeTurns) {
    if (!activeTurns[key]?.includes(agentId)) continue
    const { topicId, threadId } = parseStreamKey(key)
    if (!topicById.has(topicId)) continue
    const tool = streamTools[key]?.find(
      (t) => t.agent_id === agentId && t.active,
    )
    jobs.push({ key, topicId, threadId, tool })
  }
  // Thread titles for jobs in the first job's topic (usually the open one).
  const jobTopicId = jobs[0]?.topicId ?? ''
  const { data: jobThreads } = useThreadsQuery(jobTopicId)
  const jobTitle = (j: (typeof jobs)[number]) => {
    const t = jobThreads?.threads.find((th) => th.thread_id === j.threadId)
    return (
      t?.title ||
      t?.root_preview ||
      (topicById.get(j.topicId)?.name ?? profilesCopy.agent.topicFallback)
    )
  }

  useEffect(
    () => () => {
      if (retryTimer.current) globalThis.clearTimeout(retryTimer.current)
    },
    [],
  )

  function clearLifecycleRetry() {
    if (retryTimer.current) globalThis.clearTimeout(retryTimer.current)
    retryTimer.current = undefined
  }

  function updateCachedAgent(next: AgentDefInfo) {
    qc.setQueryData(agentQueryKeys.detail(workspaceId, agentId), next)
    qc.setQueryData<AgentDefInfo[]>(
      agentQueryKeys.workspace(workspaceId, false),
      (current = []) => {
        const without = current.filter(
          (definition) => definition.agent_id !== next.agent_id,
        )
        return next.status === 'deleted' ? without : [...without, next]
      },
    )
    qc.setQueryData<AgentDefInfo[]>(
      agentQueryKeys.workspace(workspaceId, true),
      (current = []) => {
        const without = current.filter(
          (definition) => definition.agent_id !== next.agent_id,
        )
        return [...without, next]
      },
    )
  }

  function invalidateAgentLists() {
    void qc.invalidateQueries({ queryKey: ['agents', workspaceId] })
  }

  function handleLifecycleResponse(
    response: ApiResponse<AgentDefInfo>,
    announcePending: boolean,
  ) {
    updateCachedAgent(response.data)
    invalidateAgentLists()

    if (response.status === 202) {
      setPendingReconciliation(true)
      if (announcePending) {
        addToast(
          profilesCopy.agent.reconcilePending(response.data.name),
          'info',
        )
      }
      const delay = Math.max(250, (response.retryAfterSeconds ?? 1) * 1_000)
      retryTimer.current = globalThis.setTimeout(() => {
        retryTimer.current = undefined
        void reconcileAgent()
      }, delay)
      return
    }

    setPendingReconciliation(false)
    if (response.data.status === 'deleted') {
      addToast(profilesCopy.agent.deleted, 'success')
      void navigate(ROUTES.home)
    }
  }

  async function reconcileAgent() {
    clearLifecycleRetry()
    setLifecycleAction('reconcile')
    try {
      const response = await agentDefsApi.reconcileWithMeta(
        workspaceId,
        agentId,
      )
      handleLifecycleResponse(response, false)
    } catch (error) {
      addToast(
        error instanceof Error
          ? error.message
          : profilesCopy.agent.reconcileFailed,
        'error',
      )
    } finally {
      setLifecycleAction(null)
    }
  }

  async function runLifecycle(kind: ProfileLifecycleKind) {
    clearLifecycleRetry()
    setLifecycleAction(kind)
    try {
      const response =
        kind === 'delete'
          ? await agentDefsApi.deleteWithMeta(workspaceId, agentId)
          : await agentDefsApi.updateWithMeta(workspaceId, agentId, {
              status: kind === 'pause' ? 'paused' : 'active',
            })
      setConfirmingDelete(false)
      handleLifecycleResponse(response, true)
    } catch (error) {
      addToast(
        error instanceof Error
          ? error.message
          : profilesCopy.agent.updateFailed,
        'error',
      )
    } finally {
      setLifecycleAction(null)
    }
  }

  const paused = agent?.status === 'paused'
  const deleted = agent?.status === 'deleted'

  const actions = (
    <>
      {jobs.length > 0 && (
        <StatusBadge tone="info">
          {profilesCopy.agent.runningCount(jobs.length)}
        </StatusBadge>
      )}
      {paused && (
        <StatusBadge tone={lifecycleTone('paused')}>
          {profilesCopy.agent.paused}
        </StatusBadge>
      )}
      {deleted && (
        <StatusBadge tone="neutral">
          {profilesCopy.agent.deletedBadge}
        </StatusBadge>
      )}
      {pendingReconciliation && (
        <span role="status">
          <StatusBadge tone={lifecycleTone('reconciling')}>
            {profilesCopy.agent.reconciling}
          </StatusBadge>
        </span>
      )}
      {canManage && (
        <>
          {pendingReconciliation ? (
            <Button
              variant="outline"
              loading={lifecycleAction === 'reconcile'}
              onClick={() => void reconcileAgent()}
            >
              {profilesCopy.agent.retryNow}
            </Button>
          ) : (
            <Button
              variant="outline"
              loading={
                lifecycleAction === 'pause' || lifecycleAction === 'restore'
              }
              disabled={
                lifecycleAction === 'delete' || lifecycleAction === 'reconcile'
              }
              onClick={() =>
                void runLifecycle(paused || deleted ? 'restore' : 'pause')
              }
            >
              {paused || deleted
                ? profilesCopy.agent.restore
                : profilesCopy.agent.pause}
            </Button>
          )}
          {!deleted &&
            // A link while editing is available; a link cannot be disabled,
            // so the unavailable state keeps the disabled button.
            (lifecycleBusy || pendingReconciliation ? (
              <Button variant="outline" disabled>
                {profilesCopy.agent.edit}
              </Button>
            ) : (
              <Button
                variant="outline"
                href={ROUTES.agentEdit(workspaceId, agentId)}
              >
                {profilesCopy.agent.edit}
              </Button>
            ))}
          {!deleted && (
            <Button
              variant="outline"
              disabled={lifecycleBusy || pendingReconciliation}
              onClick={() => setConfirmingDelete(true)}
            >
              {profilesCopy.agent.delete}
            </Button>
          )}
        </>
      )}
    </>
  )

  return (
    <WsPageShell
      crumbs={[
        {
          label: ws?.name ?? profilesCopy.workspaceCrumb,
          to: ROUTES.home,
        },
        { label: agent?.name ?? profilesCopy.agentTitle },
      ]}
      lead={
        agent ? <Avatar name={agent.name} kind="agent" size="lg" /> : undefined
      }
      title={agent?.name ?? profilesCopy.agentTitle}
      meta={
        agent
          ? profilesCopy.agent.meta(
              agent.model,
              formatRelative(agent.created_at, now),
            )
          : undefined
      }
      actions={agent ? actions : undefined}
    >
      <Stack gap="space-24">
        {agentQuery.isPending && (
          <div aria-busy="true">
            {showAgentSkeleton && <Skeleton lines={2} />}
          </div>
        )}
        {agentQuery.isError && (
          <Alert tone={agent ? 'warning' : 'danger'}>
            <Inline gap="space-8" align="baseline">
              <Text>
                {agent
                  ? profilesCopy.refreshAgentFailed
                  : profilesCopy.loadAgentFailed}
              </Text>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void agentQuery.refetch()}
              >
                {profilesCopy.retry}
              </Button>
            </Inline>
          </Alert>
        )}

        {agent?.description && (
          <Text as="p" tone="text-sub">
            {agent.description}
          </Text>
        )}

        <Stack as="section" gap="space-12">
          <SectionHeader rule>{profilesCopy.agent.runningNow}</SectionHeader>
          {jobs.length === 0 ? (
            <EmptyState
              variant="inline"
              description={profilesCopy.agent.nothingRunning}
            />
          ) : (
            <Card>
              <Stack gap="space-8">
                {jobs.map((j) => (
                  <Inline key={j.key} gap="space-8" align="baseline">
                    <Text tone="text-sub">
                      <StatusDot
                        tone={lifecycleTone('running')}
                        label={profilesCopy.agent.runningDot}
                      />
                    </Text>
                    <Link
                      variant="quiet"
                      href={
                        j.threadId
                          ? ROUTES.topicThread(j.topicId, j.threadId)
                          : ROUTES.topic(j.topicId)
                      }
                      className={styles.grow}
                    >
                      {jobTitle(j)}
                    </Link>
                    {j.tool && (
                      <Text variant="caption" tone="text-mute">
                        {profilesCopy.callingTool(j.tool.tool_name)}
                      </Text>
                    )}
                  </Inline>
                ))}
              </Stack>
            </Card>
          )}
        </Stack>

        <Stack as="section" gap="space-12">
          <SectionHeader rule>{profilesCopy.agent.runs}</SectionHeader>
          <RunsExplorer
            workspaceId={workspaceId}
            agentId={agentId}
            queryScope={`agent-${agentId}`}
            caption={runsCopy.caption.agent}
            returnTo={ROUTES.agentProfile(workspaceId, agentId)}
          />
        </Stack>

        <AgentMemorySection
          wsId={workspaceId}
          agentId={agentId}
          canManage={canManage}
        />
      </Stack>

      <ConfirmDialog
        open={confirmingDelete}
        title={profilesCopy.agent.deleteTitle}
        tone="default"
        description={profilesCopy.agent.deleteDescription}
        confirmLabel={profilesCopy.agent.deleteConfirm}
        loading={lifecycleBusy}
        onConfirm={() => void runLifecycle('delete')}
        onClose={() => setConfirmingDelete(false)}
      />
    </WsPageShell>
  )
}
